import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/components/ui/use-toast';
import { useProjectRole } from '@/hooks/useProjectRole';
import {
  RESERVED_STATUSES,
  projectStatusColor,
  projectStatusLabel,
  quickStatusOptions,
  showsProjectStatus,
} from '@/lib/projectStatus';

/** What the "are you sure" dialog says for a move into or out of Complete or
 *  Canceled. Leaving either is reopening the project. */
function confirmation(project, status) {
  if (status === 'complete') {
    return {
      title: `Mark ${project.name} complete?`,
      description: 'The status becomes Complete and today is recorded as the completed date. '
        + 'The project stays on the Projects list and in reports - it is not closed out.',
      action: 'Mark Complete',
    };
  }
  if (status === 'cancelled') {
    return {
      title: `Cancel ${project.name}?`,
      description: 'The status becomes Canceled: the project is not going ahead. '
        + 'It leaves the dashboard but stays on the Projects list.',
      action: 'Mark Canceled',
    };
  }
  return {
    title: `Reopen ${project.name}?`,
    description: project.status === 'complete'
      ? `The status goes back to ${projectStatusLabel(status)} and the completed date is cleared.`
      : `The status goes back to ${projectStatusLabel(status)}: the project is going ahead after all.`,
    action: 'Reopen',
  };
}

/**
 * A project's status badge that changes the status when clicked. It is how a
 * CAPEX or MOE project is marked Complete, too: the Mark Complete and Reopen
 * button it replaced on the project page is gone.
 *
 * Before this the status was set only on the edit form, which meant opening
 * the whole form and saving every field to move a project from RFP Sent to
 * Contracted. The picker sends the status alone. Complete and Canceled - into
 * them and back out - are the Construction Manager's and the Project
 * Manager's only, and ask first; the API dates a Complete itself and clears
 * the date on the way out (`completedDateRule` in api/src/lib/repo.ts), and
 * refuses moving into or out of the reserved two from anyone else
 * (`reservedValueConditions`) whatever this shows.
 *
 * Whoever cannot edit the project - a viewer, someone not running it, anyone
 * below project_manager on an archived one - sees the plain badge, and so does
 * everyone on a closed-out project: changing its status is reopening it, which
 * is not a one-click job. A tenant project shows no status at all.
 *
 * The tile and the list row are each one big link to the project, so clicks
 * inside the picker are stopped here. React carries events from the portalled
 * menu and dialog up through this span too, which is what keeps choosing an
 * option from also opening the project.
 */
export default function StatusPicker({ project, className = '' }) {
  const perms = useProjectRole(project);
  const [confirming, setConfirming] = useState(null);
  const qc = useQueryClient();
  const { toast } = useToast();

  const mutation = useMutation({
    mutationFn: status => base44.entities.Project.update(project.id, { status }),
    onSuccess: (_, status) => {
      qc.invalidateQueries({ queryKey: ['project', project.id] });
      qc.invalidateQueries({ queryKey: ['projects'] });
      toast({
        title: `Status changed to ${projectStatusLabel(status)}`,
        description: status === 'complete'
          ? 'Today is recorded as its completed date. Close it out when the paperwork is done.'
          : project.status === 'complete'
            ? 'It has been reopened and its completed date cleared.'
            : project.name,
      });
      setConfirming(null);
    },
    onError: err => {
      toast({
        title: 'Could not change the status',
        description: err.message || 'Something went wrong.',
        variant: 'destructive',
      });
      setConfirming(null);
    },
  });

  if (!showsProjectStatus(project)) return null;

  const badge = (
    <Badge className={`text-[10px] ${projectStatusColor(project.status)} ${className}`}>
      {projectStatusLabel(project.status)}
    </Badge>
  );
  if (!perms.isLoaded || !perms.canEditProject || project.archived) return badge;
  /* Reopening is the administrators' as well: a Complete or Canceled project
     is a plain badge to everyone else, as it was when Reopen was a button. */
  const reserved = RESERVED_STATUSES.includes(project.status);
  if (reserved && !perms.canCompleteProject) return badge;

  const choose = status => {
    if (status === project.status) return;
    if (reserved || RESERVED_STATUSES.includes(status)) setConfirming(status);
    else mutation.mutate(status);
  };
  const ask = confirming && confirmation(project, confirming);

  return (
    <span
      className="inline-flex"
      onClick={e => { e.preventDefault(); e.stopPropagation(); }}
    >
      <Select value={project.status} onValueChange={choose} disabled={mutation.isPending}>
        <SelectTrigger
          aria-label={`Change status of ${project.name}`}
          title="Change status"
          className={`h-auto w-auto gap-1 rounded-full border-0 px-2.5 py-0.5 text-[10px] font-semibold shadow-none focus:ring-offset-1 ${projectStatusColor(project.status)} ${className}`}
        >
          {projectStatusLabel(project.status)}
        </SelectTrigger>
        <SelectContent>
          {quickStatusOptions(project, perms.canCompleteProject).map(s => (
            <SelectItem key={s} value={s}>{projectStatusLabel(s)}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <AlertDialog open={!!confirming} onOpenChange={open => { if (!open) setConfirming(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{ask?.title}</AlertDialogTitle>
            <AlertDialogDescription>{ask?.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Go back</AlertDialogCancel>
            <AlertDialogAction
              onClick={e => { e.preventDefault(); mutation.mutate(confirming); }}
              disabled={mutation.isPending}
            >
              {ask?.action}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </span>
  );
}
